const CHEMISTRY_STORAGE_VERSION = 2;
const CHEMISTRY_RESULT_STORAGE_KEY = 'startus:chemistry:last-result';
const CHEMISTRY_DRAFT_STORAGE_KEY = 'startus:chemistry:draft';
const CHEMISTRY_PENDING_STORAGE_KEY = 'startus:chemistry:pending';
const CHEMISTRY_RESULT_TTL_MS = 60 * 60 * 1000;
const CHEMISTRY_DRAFT_TTL_MS = 6 * 60 * 60 * 1000;
const CHEMISTRY_PENDING_TTL_MS = 20 * 60 * 1000;

function getStorage() {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.localStorage;
}

function readJson(key) {
  try {
    const storage = getStorage();
    const raw = storage?.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeJson(key, value) {
  try {
    const storage = getStorage();
    storage?.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage failures and let the flow continue.
  }
}

function removeValue(key) {
  try {
    const storage = getStorage();
    storage?.removeItem(key);
  } catch {
    // Ignore storage cleanup failures.
  }
}

function createIssue(status) {
  return { status, isRecoverable: status === 'expired' || status === 'corrupt' || status === 'version_mismatch' };
}

function isUsableImageSource(source) {
  return typeof source === 'string' && source.trim().length > 0 && !source.startsWith('blob:');
}

function hasAnyDraftData(payload) {
  return Boolean(
    (payload?.nameA && payload.nameA.trim()) ||
    (payload?.nameB && payload.nameB.trim()) ||
    isUsableImageSource(payload?.previewA) ||
    isUsableImageSource(payload?.previewB)
  );
}

function isValidDraftPayload(payload) {
  return Boolean(
    payload &&
    typeof payload.nameA === 'string' &&
    typeof payload.nameB === 'string' &&
    typeof payload.previewA === 'string' &&
    typeof payload.previewB === 'string'
  );
}

function isValidPendingPayload(payload) {
  return isValidDraftPayload(payload);
}

function isValidChemistryPayload(payload) {
  return Boolean(
    payload?.data &&
    (payload?.data?.user_names?.a || payload?.user_names?.a) &&
    (payload?.data?.user_names?.b || payload?.user_names?.b)
  );
}

function buildEnvelope({ key, status, payload, sessionId, ttlMs }) {
  const savedAt = Date.now();
  return {
    key,
    version: CHEMISTRY_STORAGE_VERSION,
    status,
    sessionId: sessionId || createChemistrySessionId(),
    savedAt,
    expiresAt: savedAt + ttlMs,
    payload,
  };
}

function readEnvelope({ key, validate }) {
  const parsed = readJson(key);
  if (!parsed) {
    return { status: 'missing', record: null, issue: null };
  }

  if (parsed.version !== CHEMISTRY_STORAGE_VERSION) {
    removeValue(key);
    return { status: 'version_mismatch', record: null, issue: createIssue('version_mismatch') };
  }

  if (!parsed.expiresAt || Date.now() > parsed.expiresAt) {
    removeValue(key);
    return { status: 'expired', record: null, issue: createIssue('expired') };
  }

  if (!validate(parsed.payload)) {
    removeValue(key);
    return { status: 'corrupt', record: null, issue: createIssue('corrupt') };
  }

  return { status: 'available', record: parsed, issue: null };
}

function normalizeDraftRecord(record) {
  if (!record) {
    return null;
  }

  return {
    sessionId: record.sessionId,
    savedAt: record.savedAt,
    nameA: record.payload.nameA || '',
    nameB: record.payload.nameB || '',
    previewA: record.payload.previewA || '',
    previewB: record.payload.previewB || '',
  };
}

function normalizeResultRecord(record) {
  if (!record) {
    return null;
  }

  return {
    ...record.payload,
    sessionId: record.sessionId,
    timestamp: record.savedAt,
  };
}

function mapEnvelopeReadResult(envelopeResult, normalize) {
  if (envelopeResult.status !== 'available') {
    return {
      status: envelopeResult.status,
      data: null,
      issue: envelopeResult.issue,
    };
  }

  return {
    status: 'available',
    data: normalize(envelopeResult.record),
    issue: null,
  };
}

export function createChemistrySessionId() {
  return `chemistry-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function buildChemistryRecoveryPayload({ data, previewA, previewB, nameA, nameB }) {
  return {
    data,
    previewA,
    previewB,
    imageA: previewA,
    imageB: previewB,
    user_names: data?.user_names || { a: nameA || '', b: nameB || '' },
  };
}

export function readStoredChemistryResult() {
  return mapEnvelopeReadResult(
    readEnvelope({ key: CHEMISTRY_RESULT_STORAGE_KEY, validate: isValidChemistryPayload }),
    normalizeResultRecord
  );
}

export function writeStoredChemistryResult(payload, options = {}) {
  if (!isValidChemistryPayload(payload)) {
    return null;
  }

  const envelope = buildEnvelope({
    key: CHEMISTRY_RESULT_STORAGE_KEY,
    status: 'result',
    payload,
    sessionId: options.sessionId,
    ttlMs: CHEMISTRY_RESULT_TTL_MS,
  });

  writeJson(CHEMISTRY_RESULT_STORAGE_KEY, envelope);
  clearChemistryPending();

  return envelope.sessionId;
}

export function clearStoredChemistryResult() {
  removeValue(CHEMISTRY_RESULT_STORAGE_KEY);
}

export function resolveChemistryImageSource(state, data, restored, side) {
  const candidates = side === 'A'
    ? [
        restored?.previewA,
        restored?.imageA,
        state?.previewA,
        state?.imageA,
        state?.userImage,
        data?.input_images?.a,
        data?.images?.a,
        data?.images?.user_a,
      ]
    : [
        restored?.previewB,
        restored?.imageB,
        state?.previewB,
        state?.imageB,
        state?.partnerImage,
        data?.input_images?.b,
        data?.images?.b,
        data?.images?.user_b,
      ];

  return candidates.find(isUsableImageSource) || null;
}

export function resolveChemistryMeshOverlay(state, data, restored, side) {
  const candidates = side === 'A'
    ? [
        data?.mesh_overlays?.a,
        restored?.meshOverlays?.a,
        restored?.mesh_overlays?.a,
        state?.meshOverlays?.a,
        state?.mesh_overlays?.a,
      ]
    : [
        data?.mesh_overlays?.b,
        restored?.meshOverlays?.b,
        restored?.mesh_overlays?.b,
        state?.meshOverlays?.b,
        state?.mesh_overlays?.b,
      ];

  return candidates.find(isUsableImageSource) || null;
}

export function buildRestorableChemistryPayload(state, data, restored) {
  const nextData = state?.data || data || restored?.data || null;
  const nextPayload = {
    data: nextData,
    previewA: resolveChemistryImageSource(state, nextData, restored, 'A'),
    previewB: resolveChemistryImageSource(state, nextData, restored, 'B'),
    imageA: resolveChemistryImageSource(state, nextData, restored, 'A'),
    imageB: resolveChemistryImageSource(state, nextData, restored, 'B'),
    input_images: nextData?.input_images || restored?.input_images || null,
    mesh_overlays: {
      a: resolveChemistryMeshOverlay(state, nextData, restored, 'A'),
      b: resolveChemistryMeshOverlay(state, nextData, restored, 'B'),
    },
    user_names: nextData?.user_names || restored?.user_names || null,
  };

  return isValidChemistryPayload(nextPayload) ? nextPayload : null;
}

export function writeChemistryDraft({ nameA, nameB, previewA, previewB, sessionId }) {
  const payload = {
    nameA: nameA || '',
    nameB: nameB || '',
    previewA: previewA || '',
    previewB: previewB || '',
  };

  if (!hasAnyDraftData(payload)) {
    clearChemistryDraft();
    return sessionId || null;
  }

  const envelope = buildEnvelope({
    key: CHEMISTRY_DRAFT_STORAGE_KEY,
    status: 'draft',
    payload,
    sessionId,
    ttlMs: CHEMISTRY_DRAFT_TTL_MS,
  });

  writeJson(CHEMISTRY_DRAFT_STORAGE_KEY, envelope);
  return envelope.sessionId;
}

export function readChemistryDraft() {
  return mapEnvelopeReadResult(
    readEnvelope({ key: CHEMISTRY_DRAFT_STORAGE_KEY, validate: isValidDraftPayload }),
    normalizeDraftRecord
  );
}

export function clearChemistryDraft() {
  removeValue(CHEMISTRY_DRAFT_STORAGE_KEY);
}

export function writeChemistryPending({ nameA, nameB, previewA, previewB, sessionId }) {
  const payload = {
    nameA: nameA || '',
    nameB: nameB || '',
    previewA: previewA || '',
    previewB: previewB || '',
  };

  if (!isValidPendingPayload(payload) || !hasAnyDraftData(payload)) {
    return sessionId || null;
  }

  const envelope = buildEnvelope({
    key: CHEMISTRY_PENDING_STORAGE_KEY,
    status: 'pending',
    payload,
    sessionId,
    ttlMs: CHEMISTRY_PENDING_TTL_MS,
  });

  writeJson(CHEMISTRY_PENDING_STORAGE_KEY, envelope);
  return envelope.sessionId;
}

export function readChemistryPending() {
  return mapEnvelopeReadResult(
    readEnvelope({ key: CHEMISTRY_PENDING_STORAGE_KEY, validate: isValidPendingPayload }),
    normalizeDraftRecord
  );
}

export function clearChemistryPending() {
  removeValue(CHEMISTRY_PENDING_STORAGE_KEY);
}

export function resetChemistryRecovery() {
  clearChemistryDraft();
  clearChemistryPending();
  clearStoredChemistryResult();
}
